// deps
import React, { memo, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

// external-deps
import { isEmpty } from "@swiggy-private/js-utils";

// components
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";
import { default as IconChevronVariant } from "@swiggy-private/dls-web-icons/dist/web/generic/chevronRight/16";
import { default as IconChevronVariantSmall } from "@swiggy-private/dls-web-icons/dist/web/generic/chevronRight/12";
import { default as TickIcon } from "@swiggy-private/dls-web-icons/dist/web/generic/tickFilled/20";


// hooks
import { useTheme } from "@swiggy-private/dls-web-theme/dist/components/ThemeProvider/use-theme";

// types
import { IRewardsPoolWidget, RewardsPoolWidgetState } from "@swiggy-private/checkout-core";
import { PoolWidgetColor } from "@dineout-cart-ui/interfaces";

// constants
import {
    POOL_WIDGET_TYPE_COLOR_MAP,
    WIDGET_ICON_ALT,
    WIDGET_ICON_CLOUDINARY_ID,
    WIDGET_ICON_HEIGHT,
    WIDGET_ICON_WIDTH,
} from "@dineout-cart-ui/constants";

// helpers
import { DINE_CASH_POOLING_TEST_IDS } from "@dineout-cart-ui/helpers/TestIds";

// styles
import {
    DineCashPoolingWidgetContainer,
    WidgetLeftSection,
    IconContainer,
    ArrowContainer,
    WidgetMiddleSection,
    TitleContainer,
    SubtitleContainer,
    WidgetRightSection,
    Tag,
    PooledAmount,
    Shimmer,
    TooltipContent,
    Arrow,
    TooltipContainer,
    ContentContainer,
    Container,
    TitleText,
} from "./styles";

export interface IDineCashPoolingWidgetAnalytics {
    onWidgetImpression?: () => void;
    onWidgetClick?: () => void;
}
export interface DineCashPoolingWidgetProps {
    widgetData: IRewardsPoolWidget;
    onPress: () => void;
    analytics?: IDineCashPoolingWidgetAnalytics;
}

const DineCashPoolingWidget: React.FC<DineCashPoolingWidgetProps> = ({ widgetData, onPress, analytics }) => {
    const isPooledState = widgetData.state === RewardsPoolWidgetState.POOLED;
    const pooledAmountColor: PoolWidgetColor = POOL_WIDGET_TYPE_COLOR_MAP[widgetData.type] ?? "positive";
    const theme = useTheme();
    const tickIconRef = useRef<HTMLSpanElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [arrowLeft, setArrowLeft] = useState<number | null>(null);

    const updateArrowPosition = useCallback(() => {
        if (!tickIconRef.current || !containerRef.current) return;
        const iconRect = tickIconRef.current.getBoundingClientRect();
        const containerRect = containerRef.current.getBoundingClientRect();
        const iconCenterX = iconRect.left + iconRect.width / 2 - containerRect.left;
        setArrowLeft(iconCenterX);
    }, []);

    useLayoutEffect(() => {
        updateArrowPosition();
    }, [isPooledState, updateArrowPosition]);

    useEffect(() => {
        analytics?.onWidgetImpression?.();
    }, [
        widgetData.state,
        widgetData.noOfCurrentContributors,
        widgetData.pooledAmount,
        widgetData.type,
        widgetData.toolTip?.text,
        widgetData.flowType,
        widgetData.title,
        widgetData.subtitle,
        widgetData.tag,
        analytics,
    ]);

    const handleDinecashWidgetClick = useCallback(() => {
        analytics?.onWidgetClick?.();
        onPress();
    }, [analytics, onPress]);

    return (
        <Container>
            {!isPooledState && !isEmpty(widgetData?.tag) && <Shimmer />}
            <DineCashPoolingWidgetContainer
                data-testid={DINE_CASH_POOLING_TEST_IDS.WIDGET_CONTAINER}
                onClick={handleDinecashWidgetClick}
                ref={containerRef}
            >
                <ContentContainer>
                    <WidgetLeftSection>
                        <IconContainer>
                            <Image
                                cloudinaryId={WIDGET_ICON_CLOUDINARY_ID}
                                alt={WIDGET_ICON_ALT}
                                width={WIDGET_ICON_WIDTH}
                                height={WIDGET_ICON_HEIGHT}
                            />
                        </IconContainer>
                    </WidgetLeftSection>
                    <WidgetMiddleSection>
                        <TitleContainer data-testid={DINE_CASH_POOLING_TEST_IDS.WIDGET_TITLE}>
                            <TitleText
                                width="max-content"
                                maxLines={1}
                                variant="V2.body1.neutral.bold"
                                color="text_Highest_Emphasis"
                            >
                                {widgetData?.title || ""}
                            </TitleText>
                            {!isEmpty(widgetData?.tag) && !isPooledState && (
                                <Tag data-testid={DINE_CASH_POOLING_TEST_IDS.WIDGET_TAG}>
                                    <Text
                                        maxLines={1}
                                        variant="V2.caption.neutral.black"
                                        color="text_inverse_Highest_Emphasis"
                                    >
                                        {widgetData.tag?.toUpperCase()}
                                    </Text>
                                </Tag>
                            )}
                        </TitleContainer>
                        <SubtitleContainer data-testid={DINE_CASH_POOLING_TEST_IDS.WIDGET_SUBTITLE}>
                            <Text maxLines={1} variant="V2.overline.neutral.medium" color="text_Med_Emphasis">
                                {widgetData?.subtitle || ""}
                            </Text>
                            {isPooledState && (
                                <IconChevronVariantSmall
                                    fill={theme.colors[theme.colorMode]["text_Med_Emphasis"]}
                                />
                            )}
                        </SubtitleContainer>
                    </WidgetMiddleSection>

                    <WidgetRightSection>
                        {!isPooledState && (
                            <ArrowContainer>
                                <IconChevronVariant
                                    fill={theme.colors[theme.colorMode]["text_Med_Emphasis"]}
                                />
                            </ArrowContainer>
                        )}
                        {!isEmpty(widgetData?.pooledAmount) && isPooledState && (
                            <PooledAmount data-testid={DINE_CASH_POOLING_TEST_IDS.WIDGET_POOLED_AMOUNT}>
                                <span ref={tickIconRef}>
                                    <TickIcon fill={theme.colors[theme.colorMode][pooledAmountColor]} />
                                </span>
                                <Text maxLines={1} variant="V2.body1.neutral.bold" color={pooledAmountColor}>
                                    {widgetData.pooledAmount}
                                </Text>
                            </PooledAmount>
                        )}
                    </WidgetRightSection>
                </ContentContainer>
                {!isEmpty(widgetData?.toolTip?.text) && (
                    <TooltipContainer>
                        {!isEmpty(tickIconRef.current) && <Arrow left={arrowLeft} />}
                        <TooltipContent data-testid={DINE_CASH_POOLING_TEST_IDS.WIDGET_TOOLTIP_TEXT}>
                            <Text maxLines={1} variant="V2.overline.neutral.medium" color="warning">
                                {widgetData?.toolTip?.text}
                            </Text>
                        </TooltipContent>
                    </TooltipContainer>
                )}
            </DineCashPoolingWidgetContainer>
        </Container>
    );
};

export default memo(DineCashPoolingWidget);
